import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./dashboard.css";
import "./dashboard-shell.css";

const navItems = [
  { label: "Overview", icon: "fa-solid fa-chart-line", path: "/dashboard" },
  { label: "Members", icon: "fa-solid fa-users", path: "/dashboard/members" },
  { label: "Volunteers", icon: "fa-solid fa-hand-holding-heart", path: "/dashboard/volunteers" },
  { label: "Donations", icon: "fa-regular fa-heart", path: "/dashboard/donations" },
  { label: "Blogs", icon: "fa-regular fa-pen-to-square", path: "/dashboard/blogs" },
  { label: "Events", icon: "fa-regular fa-calendar-days", path: "/dashboard/events" },
  { label: "Testimonials", icon: "fa-regular fa-comment-dots", path: "/dashboard/testimonials" },
  { label: "Notifications", icon: "fa-regular fa-bell", path: "/dashboard/notifications" },
  { label: "Change Password", icon: "fa-solid fa-lock", path: "/dashboard/change-password" },
];

const DashboardLayout: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const sidebar = useRef<HTMLElement>(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin");
    }
  }, [navigate]);

  useEffect(() => {
    if (!menuOpen) return;
    closeButton.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuButton.current?.focus();
      } else if (event.key === "Tab") {
        const items = sidebar.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
        if (!items?.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  const isActive = (path: string) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    return location.pathname.startsWith(path);
  };

  const pageTitle = navItems.find((item) => isActive(item.path))?.label || "Dashboard";

  return (
    <div className="dashboard-shell sf-admin-shell">
      {menuOpen && <button type="button" className="sf-admin-backdrop" aria-label="Close navigation" onClick={() => { setMenuOpen(false); menuButton.current?.focus(); }} />}
      <aside ref={sidebar} id="admin-sidebar" className={`sf-admin-sidebar${menuOpen ? " is-open" : ""}`}>
          <div className="sf-admin-sidebar-brand">
            <Link to="/dashboard" className="sf-admin-brand" onClick={() => setMenuOpen(false)}>
              <img src={`${process.env.PUBLIC_URL}/foundation-icon.svg`} alt="" width="42" height="42" />
              <span><strong>Srisailam Foundation</strong><small>Administration</small></span>
            </Link>
            <button ref={closeButton} type="button" className="sf-admin-close" aria-label="Close navigation" onClick={() => { setMenuOpen(false); menuButton.current?.focus(); }}>×</button>
          </div>
          <p className="sf-admin-nav-label">MANAGEMENT</p>
          <nav className="sf-admin-navigation" aria-label="Admin navigation">
            {navItems.filter((item) => item.path !== "/dashboard/blogs" && item.path !== "/dashboard/events" && item.path !== "/dashboard/notifications").map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sf-admin-nav-link ${isActive(item.path) ? "active" : ""}`}
                aria-current={isActive(item.path) ? "page" : undefined}
                onClick={() => setMenuOpen(false)}
              >
                <i className={item.icon} aria-hidden="true" />
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              className="sf-admin-nav-link sf-admin-logout"
              onClick={() => {
                localStorage.removeItem("token");
                navigate("/admin");
              }}
            >
              <i className="fa-solid fa-arrow-right-from-bracket" aria-hidden="true" />
              Logout
            </button>
          </nav>
      </aside>
      <div className="sf-admin-workspace">
        <header className="sf-admin-topbar">
          <button ref={menuButton} type="button" className="sf-admin-menu-button" aria-controls="admin-sidebar" aria-expanded={menuOpen} aria-label="Open navigation" onClick={() => setMenuOpen(true)}><i className="fa-solid fa-bars" aria-hidden="true" /></button>
          <div><span className="sf-admin-context">Administration /</span> <strong>{pageTitle}</strong></div>
          <span className="sf-admin-topbar-label">Srisailam Foundation</span>
        </header>
        <main className="dashboard-main">{children}</main>
      </div>
    </div>
  );
};

export default DashboardLayout;
